import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Gauge, SlidersHorizontal } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { Dialog } from '@/components/ui/dialog'
import { Input, Field } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useToast } from '@/providers/UiProviders'
import { fetchBotsForMod, setBotRateLimit, BOT_MOD_STR as S, type BotModRow } from './moderationApi'

/**
 * A5.07 — Rate limit sozlamalari (individual). LIST = REAL GET /admin/bots.
 * Har bot uchun shaxsiy limit → POST /admin/bots/:id/rate-limit (lane-A greenfield,
 * deploy'gacha 404 → xatolik toast'i). 0 = standart tarif.
 */
export function RateLimitSettingsScreen() {
  const { toast } = useToast()
  const [target, setTarget] = useState<BotModRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const botLabel = (r: BotModRow) => (r.username ? `@${r.username}` : r.first_name || `#${r.id}`)

  const columns = useMemo<AdminColumn<BotModRow>[]>(
    () => [
      {
        key: 'username',
        header: S.bot,
        type: 'text',
        minWidth: 180,
        render: (r) => (
          <Link to={`/bots/${r.id}`} className="text-accent hover:underline">
            {botLabel(r)}
          </Link>
        ),
      },
      { key: 'first_name', header: S.name, type: 'text', minWidth: 160 },
      {
        key: 'rate_per_minute',
        header: S.perMinute,
        type: 'number',
        width: 130,
        align: 'right',
        render: (r) => <span className="tabular">{r.rate_per_minute ? r.rate_per_minute : '—'}</span>,
      },
      {
        key: 'rate_burst',
        header: S.burst,
        type: 'number',
        width: 110,
        align: 'right',
        render: (r) => <span className="tabular">{r.rate_burst ? r.rate_burst : '—'}</span>,
      },
      { key: 'created_at', header: S.registered, type: 'date', width: 150 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<BotModRow>[]>(
    () => [
      {
        id: 'set',
        label: S.setLimit,
        icon: <SlidersHorizontal size={13} />,
        onSelect: (r) => setTarget(r),
      },
    ],
    [],
  )

  const fetcher = useCallback(fetchBotsForMod, [])

  return (
    <>
      <PageHeader title={S.rateLimits} screenId="A5.07" />
      <DataTable<BotModRow>
        key={reloadKey}
        screenId="A5.07"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Gauge size={28} strokeWidth={1.5} />, title: S.empty }}
      />

      {target && (
        <RateLimitDialog
          row={target}
          onClose={() => setTarget(null)}
          onSaved={() => {
            setTarget(null)
            setReloadKey((k) => k + 1)
            toast(S.limitSaved, { tone: 'success' })
          }}
          onError={(e) =>
            toast(S.failed, {
              tone: 'error',
              ...(e instanceof Error && e.message ? { description: e.message } : {}),
            })
          }
        />
      )}
    </>
  )
}

function RateLimitDialog({
  row,
  onClose,
  onSaved,
  onError,
}: {
  row: BotModRow
  onClose: () => void
  onSaved: () => void
  onError: (e: unknown) => void
}) {
  const [perMinute, setPerMinute] = useState(String(row.rate_per_minute ?? 0))
  const [burst, setBurst] = useState(String(row.rate_burst ?? 0))
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      await setBotRateLimit(row.id, {
        per_minute: parseInt(perMinute, 10) || 0,
        burst: parseInt(burst, 10) || 0,
      })
      onSaved()
    } catch (e) {
      setBusy(false)
      onError(e)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${S.setLimit} — ${row.username ? `@${row.username}` : `#${row.id}`}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {S.cancel}
          </Button>
          <Button onClick={save} disabled={busy}>
            {S.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs text-tertiary">{S.rateLimitHint}</p>
        <Field label={S.perMinute}>
          <Input value={perMinute} onChange={(e) => setPerMinute(e.target.value)} inputMode="numeric" autoFocus />
        </Field>
        <Field label={S.burst}>
          <Input value={burst} onChange={(e) => setBurst(e.target.value)} inputMode="numeric" />
        </Field>
      </div>
    </Dialog>
  )
}
